/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */

import {
  BoxBufferGeometry,
  Mesh,
  Vector3,
} from 'three';

import {
  CapNames,
  DefaultCaps,
  Director,
  Materials,
  Stage,
  DefaultNavigators,
  FloorKind,
  SceneCompass,
  FloorDesc,
  CapDataDesc,
} from '@sick-davinci/glow';

import { Checkbox } from '@sick-davinci/basic-elements';

export const dependencies = [Checkbox];  // avoid wepack removal

const scs: Array<SceneCompass> = []; // store instances for gui access

/**
 * Demo:    SceneCompassDemo
 * Description:
 *  This demo application shows how to use the SceneCompass with multiple viewports and cameras.
 * Features:
 *  - Orbit navigation
 *  - Capability handling
 *  - SceneCompass with custom settings
 *  - Multiple viewports
 */
export function run(viewParent: HTMLElement) {

  // * Setup the stage *
  const stage = new Stage(viewParent, {
    cameras: [{
      name: 'primary',
      from: [0, 5, 20],
      to: [0, 5, 0],
      up: [0, 1, 0],
      fov: 50,
    }, {
      name: 'secondary',
      from: [20, 5, 0],
      to: [0, 5, 0],
      up: [0, -1, 0],
      fov: 70,
    }],
    scenes: [{ name: 'default' }],
    viewports: [{
      name: 'primary',
      scene: 'default',
      camera: 'primary',
      navigator: 'orbit',
      bgcolor: 0x393939,
    }, {
      name: 'secondary',
      scene: 'default',
      camera: 'secondary',
      navigator: 'orbit2',
      rightAbs: 10,
      bottomAbs: 100,
      widthAbs: 320,
      heightAbs: 240,
      bgcolor: 0x292929,
    }],
  });

  // * Add 2 scene compasses
  scs.push(SceneCompass.create(stage, 'primary', 'primary')); // by default it is interactive and is displayed in the upper right corner

  scs.push(SceneCompass.create(stage, 'secondary', 'secondary', {
    interactive: true,
    leftAbs: 10,
    topAbs: 10,
    widthAbs: 80,
    heightAbs: 80,
  }));

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'orbit');

  DefaultNavigators.initOrbit(stage, 'orbit2', {
    rotateAround: {
      up: new Vector3(0, -1, 0),
    },
  });

  // * Data handling using capabilities
  const director = new Director(stage);

  DefaultCaps.init(director);

  director.handle(CapNames.LIGHT, { lightType: 'point', color: 0xfffff0, orientation: { position: [1500, 800, -1300] } });
  director.handle(CapNames.LIGHT, { lightType: 'ambient', color: 0x888888 });
  director.handle(CapNames.AXIS, { axisLength: 10 });

  director.handle(CapNames.FLOOR, {
    payload: {
      floor: FloorKind.WIREFRAME,
      size: 256,
      segments: 128,
      color: 0x555555,
    },
    orientation: {
      position: [0, -.01, 0],
    },
  } as CapDataDesc<FloorDesc>);

  // * Add a box using native threejs code
  stage.applyThreeDirect((scene) => {
    scene.add(createBox(0xe0e0e0, [10, 10, 10], [-10, 5, 0]));
  });

  stage.start();  // start visualizing data

  // Connect GUI
  document.querySelectorAll('.showSceneCompass').forEach((el: Checkbox) => {
    el.addEventListener('change', (event: CustomEvent) => {
      const idx = parseInt((event.target as HTMLElement).getAttribute('data-scene-compass-idx'), 10);
      scs[idx].toggleVisibility(event.detail);
    });
  });

} // end of setup()

function createBox(color: number, size: [number, number, number], position: [number, number, number]) {
  const box =
    new Mesh(
      new BoxBufferGeometry(...size),
      Materials.create({ type: 'normal' }),
    );
  box.position.set(...position);
  box.updateMatrix();
  return box;
}
